import { Helmet } from 'react-helmet-async';
import ContactUsComponent from '../../components/ContactUsComponent'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faDiagramProject,
    faUsers,
    faListCheck,
    faClock,
    faChartLine,
    faLayerGroup,
    faEye,
    faChartSimple,
    faCheckCircle,
    faGlobe
} from '@fortawesome/free-solid-svg-icons'

const technologiesIcon = [
    "../images/react.png",
    "../images/azure.svg",
    "../images/azure-sql.png",
    "../images/nodejs.png",
    "../images/csharp.png",
];

const ProjectManagement = () => {
    return (
        <>
            <Helmet>
                <title>JJ Kit Case Study | Project Management Software by Data Repo</title>
                <meta
                    name="description"
                    content="Discover how Data Repo built JJ Kit, a project management software that helps teams plan tasks, track progress, collaborate efficiently, and manage projects from start to finish."
                />
            </Helmet>

            <section
                style={{
                    backgroundImage: `url(../images/bg-casestudies-box.png)`,
                    backgroundSize: "100% 80vh"
                }}
                className="relative flex flex-col justify-center w-full h-[65vh] bg-no-repeat bg-fixed pt-[65px] text-center text-white">
                <h1 className="text-3xl md:text-6xl font-bold uppercase">JJ Kit</h1>
                <p className="mt-6 text-lg md:text-2xl max-w-3xl mx-auto">
                    Project Management Software Developed by Data Repo
                </p>
            </section>

            <section className="bg-gray-100 py-16 px-6 md:px-20">
                <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
                    <div>
                        <h2 className="text-3xl font-bold mb-6">Bringing Structure to Team Projects</h2>
                        <p className="text-lg text-gray-700 mb-4">
                            Teams often struggle to keep projects organized when tasks, deadlines, and communication
                            are spread across multiple tools or emails.
                        </p>
                        <p className="text-lg text-gray-700">
                            JJ Kit was developed by Data Repo as a centralized project management platform that helps
                            teams plan tasks, track progress, collaborate, and deliver projects on time.
                        </p>
                    </div>
                    <img src="../images/jj-kit.png" alt="Project management dashboard" className="rounded-xl shadow-lg" />
                </div>
            </section>

            <section className="bg-white py-16 px-6 md:px-20">
                <div className="max-w-6xl mx-auto text-center mb-10">
                    <h2 className="text-3xl font-bold mb-4">Inside the JJ Kit Dashboard</h2>
                    <p className="text-gray-600 max-w-3xl mx-auto">
                        JJ Kit provides teams with clear visibility into tasks, timelines,
                        and collaboration in a centralized workspace.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                    <img src="../images/jj-kit-kanban-board.png" className="rounded-xl shadow-lg" alt="jj kit" />
                    <img src="../images/jj-kit-view-report.png" className="rounded-xl shadow-lg" alt="jj kit" />
                </div>
            </section>

            <section className="bg-gradient-to-r from-blue-50 to-white py-8 px-6 md:px-20 border-y">
                <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">

                    <div className="flex items-center gap-4 justify-center md:justify-start">
                        <div className="bg-blue-100 text-blue-700 p-3 rounded-full text-xl">
                            <FontAwesomeIcon icon={faGlobe} />
                        </div>
                        <div>
                            <p className="text-sm uppercase tracking-wide text-gray-500">Live Product</p>
                            <p className="text-lg font-semibold text-gray-800">
                                JJ Kit helps teams manage projects with better clarity and collaboration
                            </p>
                        </div>
                    </div>

                    <a
                        href="https://jjkit.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-blue-800 transition"
                    >
                        Visit Website →
                    </a>
                </div>
            </section>

            <section className="bg-white py-20 px-6 md:px-20">
                <div className="max-w-6xl mx-auto text-center">
                    <h2 className="text-4xl font-bold mb-4">Project Objectives</h2>
                    <p className="text-gray-600 text-lg mb-14 max-w-3xl mx-auto">
                        JJ Kit was built to improve task organization, enhance team collaboration,
                        and provide better visibility into project timelines and progress.
                    </p>

                    <div className="grid md:grid-cols-3 gap-10">
                        <div className="bg-gray-50 p-8 rounded-2xl shadow-md">
                            <FontAwesomeIcon icon={faLayerGroup} className="text-3xl text-blue-700 mb-4" />
                            <h3 className="font-semibold text-xl mb-2">Centralized Project Control</h3>
                            <p>Manage tasks, deadlines, and resources from one platform.</p>
                        </div>

                        <div className="bg-gray-50 p-8 rounded-2xl shadow-md">
                            <FontAwesomeIcon icon={faEye} className="text-3xl text-blue-700 mb-4" />
                            <h3 className="font-semibold text-xl mb-2">Clear Progress Visibility</h3>
                            <p>Give managers and teams real-time insight into project status.</p>
                        </div>

                        <div className="bg-gray-50 p-8 rounded-2xl shadow-md">
                            <FontAwesomeIcon icon={faChartSimple} className="text-3xl text-blue-700 mb-4" />
                            <h3 className="font-semibold text-xl mb-2">Scalable Team Collaboration</h3>
                            <p>Support small teams and large departments with one system.</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-gray-100 py-16 px-6 md:px-20">
                <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
                    <img src="../images/event-manual.jpg" alt="Manual project tracking" className="rounded-xl shadow-md" />
                    <div>
                        <h2 className="text-3xl font-bold mb-6">Project Management Challenges</h2>
                        <ul className="list-disc pl-5 text-lg text-gray-700 space-y-3">
                            <li>Tasks tracked across emails and spreadsheets</li>
                            <li>Unclear project deadlines and responsibilities</li>
                            <li>Lack of visibility into team workload</li>
                            <li>Difficulty coordinating multiple projects</li>
                        </ul>
                    </div>
                </div>
            </section>

            <section className="bg-white py-16 px-6 md:px-20">
                <h2 className="text-3xl font-bold text-center mb-12">Core Software Modules</h2>

                <div className="grid md:grid-cols-3 gap-12 text-center">
                    <div>
                        <FontAwesomeIcon icon={faListCheck} className="text-4xl text-blue-700 mb-4" />
                        <h3 className="text-xl font-semibold mb-2">Task Management</h3>
                        <p>Create, assign, and track tasks with deadlines and priorities.</p>
                    </div>

                    <div>
                        <FontAwesomeIcon icon={faUsers} className="text-4xl text-blue-700 mb-4" />
                        <h3 className="text-xl font-semibold mb-2">Team Collaboration</h3>
                        <p>Enable teams to communicate and share updates within projects.</p>
                    </div>

                    <div>
                        <FontAwesomeIcon icon={faClock} className="text-4xl text-blue-700 mb-4" />
                        <h3 className="text-xl font-semibold mb-2">Timeline Tracking</h3>
                        <p>Monitor project milestones and deadlines in one view.</p>
                    </div>

                    <div>
                        <FontAwesomeIcon icon={faDiagramProject} className="text-4xl text-blue-700 mb-4" />
                        <h3 className="text-xl font-semibold mb-2">Project Planning</h3>
                        <p>Organize projects into phases and structured workflows.</p>
                    </div>

                    <div>
                        <FontAwesomeIcon icon={faChartLine} className="text-4xl text-blue-700 mb-4" />
                        <h3 className="text-xl font-semibold mb-2">Performance Reports</h3>
                        <p>Track project progress and team productivity through reports.</p>
                    </div>

                    <div>
                        <FontAwesomeIcon icon={faLayerGroup} className="text-4xl text-blue-700 mb-4" />
                        <h3 className="text-xl font-semibold mb-2">Multi-Project Management</h3>
                        <p>Handle multiple projects simultaneously from one dashboard.</p>
                    </div>
                </div>
            </section>

            <section className="bg-gray-100 py-16 px-6 md:px-20">
                <div className="max-w-6xl mx-auto text-center mb-10">
                    <h2 className="text-3xl font-bold mb-4">Key Features in Action</h2>
                    <p className="text-gray-600 max-w-3xl mx-auto">
                        Organize tasks, collaborate with teams, and monitor project progress in real time.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                    <img src="../images/jj-kit-add-people.png" className="rounded-xl shadow-lg" alt="jj kit" />
                    <img src="../images/jj-kit-user-stories.png" className="rounded-xl shadow-lg" alt="jj kit" />
                    <img src="../images/jj-kit-boards.png" className="rounded-xl shadow-lg" alt="jj kit" />
                    <img src="../images/jj-kit-recent-work.png" className="rounded-xl shadow-lg" alt="jj kit" />
                </div>
            </section>

            <section className="bg-gradient-to-b from-gray-100 to-white py-20 px-6 md:px-20 text-center">
                <div className="max-w-6xl mx-auto">
                    <h2 className="text-4xl font-bold mb-4">Business Impact</h2>
                    <p className="text-gray-600 text-lg mb-14 max-w-3xl mx-auto">
                        Teams using JJ Kit experienced improved task organization, better collaboration,
                        and greater visibility into project timelines and deliverables.
                    </p>

                    <div className="grid md:grid-cols-3 gap-10">
                        <div className="bg-white p-10 rounded-2xl shadow-md">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-3xl text-green-600 mb-4" />
                            <h3 className="font-semibold text-lg">Better Task Organization</h3>
                        </div>
                        <div className="bg-white p-10 rounded-2xl shadow-md">
                            <FontAwesomeIcon icon={faClock} className="text-3xl text-blue-600 mb-4" />
                            <h3 className="font-semibold text-lg">Improved Deadline Tracking</h3>
                        </div>
                        <div className="bg-white p-10 rounded-2xl shadow-md">
                            <FontAwesomeIcon icon={faChartLine} className="text-3xl text-yellow-600 mb-4" />
                            <h3 className="font-semibold text-lg">Clear Project Insights</h3>
                        </div>
                    </div>
                </div>
            </section>

            <section className="flex flex-col items-center py-16 px-8">
                <h2 className="text-4xl font-bold mb-8">Technologies Used</h2>
                <div className="flex flex-wrap gap-8 justify-center">
                    {technologiesIcon.map((item, index) => (
                        <img key={index} src={item} alt="technology" width="90" />
                    ))}
                </div>
            </section>

            <ContactUsComponent />
        </>
    )
}

export default ProjectManagement